import React, { useState, useEffect } from 'react';
import { Users, UserCheck, UserPlus, UserMinus, Clock, Activity, ChartBar, MapPin, Search } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import API_BASE_URL from '../Config';
import { useNavigate } from 'react-router-dom';

const StatCard = ({ title, value, icon: Icon, color, description }) => (
  <div className="bg-white rounded-enterprise border border-slate-100 p-6 shadow-sm hover:shadow-md transition-all duration-300">
    <div className="flex items-center justify-between mb-4">
      <div className={`p-3 rounded-xl ${color} bg-opacity-10`}>
        <Icon className={`w-6 h-6 ${color.replace('bg-', 'text-')}`} />
      </div>
      <span className="text-2xl font-black text-slate-800">{value}</span>
    </div>
    <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider">{title}</h3>
    {description && <p className="text-xs text-slate-400 mt-1">{description}</p>}
  </div>
);

export default function SocioDashboard() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/socios/stats`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (response.ok) {
          const data = await response.json();
          setStats(data);
        }
      } catch (error) {
        console.error("Error fetching socio stats:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-uni-green"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-premium-in p-6">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-slate-800 tracking-tight">Panel de Control de Membresía</h1>
          <p className="text-slate-500 font-medium">Resumen administrativo de la base societaria</p>
        </div>
        <div className="flex gap-3">
          <button 
            onClick={() => navigate('/socios/buscar')}
            className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 text-slate-600 rounded-enterprise font-bold text-sm hover:bg-slate-50 transition-colors"
          >
            <Search className="w-4 h-4" />
            Buscar Socio
          </button>
          <button 
            onClick={() => navigate('/socios/nuevo')}
            className="flex items-center gap-2 px-4 py-2.5 bg-uni-green text-white rounded-enterprise font-bold text-sm hover:bg-uni-green-dark transition-all shadow-lg shadow-uni-green/20"
          >
            <UserPlus className="w-4 h-4" />
            Nuevo Socio
          </button>
        </div>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard 
          title="Total Socios" 
          value={stats?.total || 0} 
          icon={Users} 
          color="bg-slate-500"
          description="Universo total histórico"
        />
        <StatCard 
          title="Activos" 
          value={stats?.activos || 0} 
          icon={UserCheck} 
          color="bg-emerald-500"
          description="Saldos al día y operativos"
        />
        <StatCard 
          title="Pre-Socios" 
          value={stats?.preSocios || 0} 
          icon={Clock} 
          color="bg-amber-500"
          description="Pendientes de confirmación"
        />
        <StatCard 
          title="Pend. Aprobación" 
          value={stats?.pendientesAprobacion || 0} 
          icon={Activity} 
          color="bg-indigo-500"
          description="En revisión de gerencia"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Genre Distribution Card */}
        <div className="lg:col-span-1 bg-white p-6 rounded-enterprise border border-slate-100 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <ChartBar className="w-5 h-5 text-uni-green" />
            <h3 className="font-black text-slate-800 uppercase tracking-wider text-sm">Distribución por Género</h3>
          </div>
          <div className="space-y-4">
            {Object.entries(stats?.porGenero || {}).map(([genero, cantidad]) => (
              <div key={genero} className="space-y-2">
                <div className="flex justify-between text-sm font-bold">
                  <span className="text-slate-600">{genero}</span>
                  <span className="text-slate-800">{cantidad}</span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-2">
                  <div 
                    className="bg-uni-green h-2 rounded-full transition-all duration-1000" 
                    style={{ width: `${(cantidad / (stats?.total || 1)) * 100}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* State Breakdown Card */}
        <div className="lg:col-span-2 bg-white p-6 rounded-enterprise border border-slate-100 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <Activity className="w-5 h-5 text-uni-green" />
            <h3 className="font-black text-slate-800 uppercase tracking-wider text-sm">Estado Detallado</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {Object.entries(stats?.porEstado || {}).map(([estado, cantidad]) => (
              <div key={estado} className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-1">{estado}</p>
                <p className="text-xl font-black text-slate-800">{cantidad}</p>
              </div>
            ))}
          </div>
          
          <div className="mt-8 p-4 bg-uni-green/5 border border-uni-green/10 rounded-xl flex items-center gap-4">
            <div className="p-3 bg-uni-green bg-opacity-10 rounded-full">
              <MapPin className="w-5 h-5 text-uni-green" />
            </div>
            <div>
              <p className="text-sm font-bold text-slate-800 italic">"Próximamente: Mapa de calor de distribución geográfica de socios"</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
